export enum CardState {
  pending = "pending",
  frontsideProcessing = "frontside_processing",
  frontsideFailed = "frontside_failed",
  backsideProcessing = "backside_processing",
  backsideFailed = "backside_failed",
  completed = "completed",
  error = "error",
  unknown = "unknown",
}

export const enum ScanState {
  pending = "pending",
  processing = "processing",
  rejected = "rejected",
  skipped = "skipped",
  completed = "completed",
  error = "error",
  unknown = "unknown",
}

export const enum CaptureType {
  manual = "manual",
  automatic = "automatic",
  selectedFile = "selectedFile",
}

//TODO: this needs to be implemented or deleted.
export enum CardFields {
  groupNumber = "group_number",
  payerName = "payer_name",
  memberNumber = "member_number",
  memberName = "member_name",
  dependentNames = "dependent_names",
  rxBin = "rx_bin",
  rxPCN = "rx_pcn",
  rxGroup = "rx_group",
}

export type Patient = {
  firstName: string;
  lastName: string;
  DOB: string;
};

export type FieldValue = {
  value: string;
  scores: string[];
};

export type ProgressInfo = {
  cardId: string;
  cardState: CardState;
  scanCount: number;
};

export type HealthcarePayerInfoResponse = {
  chc_payer_id: string;
  chc_payer_name: string;
  score: number;
};

export type CustomPayerInfoResponse = {
  custom_payer_id: string;
  custom_payer_name: string;
  custom_payer_name_alt: string;
  score: number;
  source: string;
};

export type HealthcarePayerInfo = {
  chcPayerId: string;
  chcPayerName: string;
  score: number;
};

export type CustomPayerInfo = {
  customPayerId: string;
  customPayerName: string;
  customPayerNameAlt: string;
  score: number;
  source: string;
};

export type CardDetailsResponse = {
  card_id: string;
  state: CardState;
  created_at?: string;
  message?: string;
  eligibility?: any;
  images?: {
    front: {
      url: string;
    };
  };
  details?: {
    member_number: FieldValue;
    group_number: FieldValue;
    payer_name: FieldValue;
    rx_bin: FieldValue;
    rx_pcn: FieldValue;
    rx_group: FieldValue;
    member_name: FieldValue;
    dependent_names: FieldValue[];
    plan_name: FieldValue;
    plan_id: FieldValue;
    client_name: FieldValue;
    start_date: FieldValue;
    card_specific_id: FieldValue;
  };
  payer_match?: {
    cardscan_payer_id?: string;
    cardscan_payer_name?: string;
    score?: number;
    change_healthcare?: HealthcarePayerInfoResponse[];
    custom?: CustomPayerInfoResponse[];
  };
};

export default class Card {
  cardId: string;
  state: CardState;
  createdAt?: string;
  message?: string;
  images?: {
    front: {
      url: string;
    };
  };
  details?: {
    memberNumber: FieldValue;
    groupNumber: FieldValue;
    payerName: FieldValue;
    rxBin: FieldValue;
    rxPCN: FieldValue;
    rxGroup: FieldValue;
    memberName: FieldValue;
    dependentNames: FieldValue[];
    planName: FieldValue;
    planId: FieldValue;
    clientName: FieldValue;
    startDate: FieldValue;
    cardSpecificId: FieldValue;
  };
  payerMatch?: {
    cardscanPayerId?: string;
    cardscanPayerName?: string;
    score?: number;
    changeHealthcare?: HealthcarePayerInfo[];
    custom?: CustomPayerInfo[];
  };

  public constructor(params: CardDetailsResponse) {
    this.cardId = params.card_id;
    this.state = params.state;
    this.createdAt = params.created_at;
    this.message = params.message ? params.message : undefined;
    this.images = params.images;
    this.details = params.details
      ? {
          memberNumber: params?.details.member_number,
          groupNumber: params.details.group_number,
          payerName: params.details.payer_name,
          rxBin: params.details.rx_bin,
          rxPCN: params.details.rx_pcn,
          rxGroup: params.details.rx_group,
          memberName: params.details.member_name,
          dependentNames: params.details.dependent_names,
          planName: params.details.plan_name,
          planId: params.details.plan_id,
          clientName: params.details.client_name,
          startDate: params.details.start_date,
          cardSpecificId: params.details.card_specific_id,
        }
      : undefined;
    this.payerMatch = params.payer_match
      ? {
          cardscanPayerId: params.payer_match.cardscan_payer_id,
          cardscanPayerName: params.payer_match.cardscan_payer_name,
          score: params.payer_match.score,
          changeHealthcare: params.payer_match.change_healthcare
            ? formatChangeHealthcare(params.payer_match.change_healthcare)
            : [],
          custom: params.payer_match.custom
            ? formatCustomPayer(params.payer_match.custom)
            : [],
        }
      : {};
  }
}

function formatChangeHealthcare(
  changeHealthcare: HealthcarePayerInfoResponse[]
): HealthcarePayerInfo[] {
  return changeHealthcare.map((data) => ({
    chcPayerId: data.chc_payer_id,
    chcPayerName: data.chc_payer_name,
    score: data.score,
  }));
}

function formatCustomPayer(
  custom: CustomPayerInfoResponse[]
): CustomPayerInfo[] {
  return custom.map((data) => ({
    customPayerId: data.custom_payer_id,
    customPayerName: data.custom_payer_name,
    customPayerNameAlt: data.custom_payer_name_alt,
    score: data.score,
    source: data.source,
  }));
}
